import {
  CompanySettings_Update,
  getV1CompaniesCompanyIdSettings,
  patchV1CompaniesCompanyIdSettings,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { pick } from 'lodash-es';
import { useCallback } from 'react';

import { CompanySettings } from '~hooks/useCompanySettings/models';
import connection from '~services/connectionModule';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';

export default function useCompanySettings() {
  const { userStore } = useStores();

  const getCompanySettings = useCallback(
    async (companyId: string) => {
      try {
        const response = await getV1CompaniesCompanyIdSettings({
          path: { 'company-id': companyId },
        });
        const companySettings = CompanySettings.parse(response.data.data);
        userStore.setCompanySettings(companySettings);

        return companySettings;
      } catch (error) {
        // A failed (re)fetch must never leave a previously-hydrated company's
        // settings applying (e.g. impersonation rehydration) — reset to the
        // fail-closed defaults (every setting false) before surfacing the error.
        userStore.setCompanySettings(new CompanySettings({}));
        connection.handleRequestError(
          error,
          t('error_messages.company_settings.failed_to_fetch'),
        );
      }
    },
    [userStore],
  );

  // Reads a company other than the user's own (the company selector's pick). Never touches the
  // store, which mirrors the user's own company only, so the values cannot linger after the
  // selector moves on.
  const getManagedCompanySettings = useCallback(
    async (companyId: string, signal?: AbortSignal) => {
      try {
        const response = await getV1CompaniesCompanyIdSettings({
          path: { 'company-id': companyId },
          signal,
        });

        return CompanySettings.parse(response.data.data);
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.company_settings.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const refreshCompanySettings = useCallback(
    async (companyId: string) => {
      try {
        const response = await getV1CompaniesCompanyIdSettings({
          path: { 'company-id': companyId },
        });
        userStore.setCompanySettings(CompanySettings.parse(response.data.data));
      } catch (error) {
        reportRequestError(error, t('error_messages.company_settings.failed_to_fetch'));
      }
    },
    [userStore],
  );

  const updateCompanySettings = useCallback(
    async (companyId: string, settings: CompanySettings_Update) => {
      try {
        const response = await patchV1CompaniesCompanyIdSettings({
          path: { 'company-id': companyId },
          body: settings,
        });
        // Hydrate only the keys this PATCH changed (values from the authoritative
        // response). Concurrent toggle saves each own their keys, so an earlier
        // full-snapshot response arriving last cannot wipe a save that already
        // applied.
        const patchedSettings = pick(
          response.data.data,
          Object.keys(settings) as (keyof CompanySettings_Update)[],
        );
        if (companyId !== userStore.userCompany.id) {
          return CompanySettings.parse(response.data.data);
        }

        const companySettings = userStore.companySettings.withSettings(patchedSettings);
        userStore.setCompanySettings(companySettings);

        return companySettings;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.company_settings.failed_to_update'),
        );
      }
    },
    [userStore],
  );

  return {
    getCompanySettings,
    getManagedCompanySettings,
    refreshCompanySettings,
    updateCompanySettings,
  };
}
